import { supabase } from "@/integrations/supabase/client";

// Google Ads compliance utilities
export const googleAdsCompliance = {
  // Enhanced bot detection for Google Ads compliance
  detectSuspiciousActivity(): boolean {
    const userAgent = navigator.userAgent;
    
    // Enhanced bot detection patterns
    const botPatterns = [
      /bot|crawler|spider|scraper/i,
      /headless|phantom|selenium/i,
      /automated|script|curl/i,
      /python|perl|ruby|wget/i
    ];
    
    const isSuspiciousUA = botPatterns.some(pattern => pattern.test(userAgent));
    
    // Check for rapid navigation patterns
    const navigationEntries = performance.getEntriesByType('navigation');
    const rapidNavigation = navigationEntries.length > 0 && 
      (navigationEntries[0] as PerformanceNavigationTiming).loadEventEnd < 100;
    
    return isSuspiciousUA || rapidNavigation;
  },

  // Consent management for GDPR/privacy compliance
  initializeConsentManagement(): void {
    if (typeof window !== 'undefined') {
      // Check for existing consent
      const consent = localStorage.getItem('ads_consent');
      
      if (!consent) {
        // Show consent banner (implement your consent UI here)
        this.showConsentBanner();
      } else {
        this.applyConsentSettings(JSON.parse(consent));
      }
    }
  },

  showConsentBanner(): void {
    // Create a simple consent banner
    const banner = document.createElement('div');
    banner.id = 'consent-banner';
    banner.style.cssText = `
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: #333;
      color: white;
      padding: 1rem;
      z-index: 10000;
      text-align: center;
    `;
    banner.innerHTML = `
      <p>We use cookies and analytics to improve your experience. 
      <button id="accept-consent" style="margin-left: 1rem; padding: 0.5rem; background: #007bff; color: white; border: none; border-radius: 4px;">
        Accept All
      </button>
      <button id="decline-consent" style="margin-left: 0.5rem; padding: 0.5rem; background: #6c757d; color: white; border: none; border-radius: 4px;">
        Decline
      </button>
      </p>
    `;
    
    document.body.appendChild(banner);
    
    // Handle consent responses
    document.getElementById('accept-consent')?.addEventListener('click', () => {
      this.setConsent({ analytics: true, advertising: true });
      banner.remove();
    });
    
    document.getElementById('decline-consent')?.addEventListener('click', () => {
      this.setConsent({ analytics: false, advertising: false });
      banner.remove();
    });
  },

  setConsent(settings: { analytics: boolean; advertising: boolean }): void {
    localStorage.setItem('ads_consent', JSON.stringify(settings));
    this.applyConsentSettings(settings);
  },

  applyConsentSettings(settings: { analytics: boolean; advertising: boolean }): void {
    // Apply consent to Google Analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': settings.analytics ? 'granted' : 'denied',
        'ad_storage': settings.advertising ? 'granted' : 'denied',
        'ad_user_data': settings.advertising ? 'granted' : 'denied',
        'ad_personalization': settings.advertising ? 'granted' : 'denied'
      });
    }

    // Apply consent to Facebook Pixel
    if (window.fbq && !settings.advertising) {
      window.fbq('consent', 'revoke');
    }
  },

  // Validate shareable links for proper structure
  validateShareableLink(link: string): boolean {
    // Ensure shareable links follow proper format to avoid redirect issues
    const urlPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/;
    return urlPattern.test(link);
  },

  // Log legitimate business activities (non-blocking)
  async logLegitimateActivity(activityType: string, details: any = {}): Promise<void> {
    // Use setTimeout to make this non-blocking for critical path
    setTimeout(async () => {
      try {
        await supabase.functions.invoke('security-headers', {
          body: { 
            action: 'log_legitimate_activity', 
            data: { 
              activity_type: activityType, 
              timestamp: new Date().toISOString(),
              legitimate_business_activity: true,
              compliance_context: 'google_ads_policy',
              ...details 
            } 
          }
        });
      } catch (error) {
        console.error('Failed to log legitimate activity:', error);
      }
    }, 0);
  },

  // Enhanced URL sanitization
  sanitizeURL(url: string): string {
    try {
      const urlObj = new URL(url);
      
      // Remove potentially suspicious parameters
      const suspiciousParams = ['redirect', 'goto', 'url', 'link', 'next', 'target'];
      suspiciousParams.forEach(param => {
        urlObj.searchParams.delete(param);
      });
      
      return urlObj.toString();
    } catch {
      return ''; // Return empty string for invalid URLs
    }
  }
};

// Initialize compliance on module load
if (typeof window !== 'undefined') {
  // Add event listener for DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      googleAdsCompliance.initializeConsentManagement();
    });
  } else {
    googleAdsCompliance.initializeConsentManagement();
  }
}

// Global type declarations for gtag and fbq
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    fbq: (...args: any[]) => void;
    gtag_consent_initialized: boolean;
  }
}

export default googleAdsCompliance;